import React, { useCallback, useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useT } from '../i18n';
import BRAND from '../brand';

/*
  Light, dark or follow the system. The chosen mode is stored under `theme`
  and read back by the bootstrap in App.js, so the page never flashes the
  wrong palette on load. In `system` mode we keep listening, so the workspace
  follows the OS when it flips at sunset.
*/

export function prefersDark() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

export function applyTheme(mode) {
  if (typeof document === 'undefined') return;
  const dark = mode === 'dark' || (mode === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
}

export function readTheme() {
  try {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch (_e) {
    /* storage unavailable */
  }
  return BRAND.defaultTheme || 'system';
}

export default function ThemeToggle() {
  const { t } = useT();
  const [mode, setMode] = useState(readTheme);

  useEffect(() => {
    applyTheme(mode);
    try {
      localStorage.setItem('theme', mode);
    } catch (_e) {
      /* storage unavailable */
    }
  }, [mode]);

  // Follow the OS while the user has chosen to.
  useEffect(() => {
    if (mode !== 'system') return undefined;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [mode]);

  const choose = useCallback((next) => setMode(next), []);

  const options = [
    { key: 'light', Icon: Sun, label: t('Light theme') },
    { key: 'dark', Icon: Moon, label: t('Dark theme') },
    { key: 'system', Icon: Monitor, label: t('Match system') },
  ];

  return (
    <div className="xp-theme-switch" role="group" aria-label={t('Theme')}>
      {options.map(({ key, Icon, label }) => (
        <button
          key={key}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={mode === key}
          onClick={() => choose(key)}
          className={'xp-theme-btn' + (mode === key ? ' is-on' : '')}
        >
          <Icon className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}
